import { GraphQLSubscriptionConfig, OperationType } from 'relay-runtime'
import { useSubscription as useRelaySubscription } from 'react-relay'
import { trackError } from 'services/analytics'
import { useCallback, useMemo, useState } from 'react'
import { Nullable } from 'types'

export function useSubscription<TSubscriptionPayload extends OperationType>(config: GraphQLSubscriptionConfig<TSubscriptionPayload>) {
  // const name = (config?.subscription as any)?.default?.params?.name || 'Unknown Subscription'
  // console.log(`GraphQL Subscription '${name}':mounted`, config?.variables)
  return useRelaySubscription<TSubscriptionPayload>({
    ...config,
    onNext: (response) => {
      // console.log(`GraphQL Subscription '${name}':onNext`, response)
      if (typeof config?.onNext === 'function') return config.onNext(response)
    },
    onCompleted: () => {
      // console.log(`GraphQL Subscription '${name}':onCompleted`)
      if (typeof config?.onCompleted === 'function') return config.onCompleted()
    },
    onError: (error) => {
      trackError(error)
      // console.log(`GraphQL Subscription '${name}':onError`, error)
      if (typeof config?.onError === 'function') return config.onError(error)
    },
  })
}

/**
 * Cleans an array from null elements and defaults it to empty array when it's null or undefined.
 * Useful for rendering list-like data coming from Relay.
 *
 * @param factory function that returns the values
 * @param deps dependencies. The return value will be memoized and recalculated when these change
 */
export function useDataList<T>(factory: () => (T[] | readonly T[]), deps: any[]): NonNullable<T>[] {
  return useMemo(() => factory()?.filter(v => Boolean(v)) as NonNullable<T>[] || [], deps)
}

/**
 * Read nodes from GraphQL return values, while automatically filtering the array and defaulting it to empty array
 * @param nodes an array of nodes to read
 */
export function useNodes<T>(nodes: Nullable<readonly T[]>) {
  const factory = useCallback(() => nodes || [], [nodes])
  return useDataList(factory, [nodes, factory])
}

/**
 * GraphQL-like Edge structure
 */
export interface Edge<T> {
  node: T
}

/**
 * Read edges from GraphQL return values, while automatically mapping and filtering the array
 * and defaulting it to empty array
 *
 * @param edges edges with a node in them
 */
export function useEdges<T>(edges: Nullable<readonly Nullable<Edge<T>>[]>) {
  const factory = useCallback(() => edges?.map(e => e?.node) || [], [edges])
  return useDataList(factory, [edges, factory])
}

export function useFetchKey() {
  const [fetchKey, setFetchKey] = useState(0)

  const increment = useCallback(() => {
    setFetchKey(value => value + 1)
  }, [])

  return useMemo(() => [
    fetchKey,
    increment,
  ] as const, [fetchKey, increment])
}
